import { LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { HomeBannersDto, HomeBannersSettingsService } from '@proxy/home-banners';
import { fileApis } from 'src/environments/environment';
import { CustomHomeBannersSettingsService } from './services/home-banners-settings.service';

@Component({
  selector: 'app-home-banners-tab',
  templateUrl: './home-banners-tab.component.html',
  styleUrls: ['./home-banners-tab.component.scss'],
})
export class HomeBannersTabComponent implements OnInit {
  form: UntypedFormGroup;
  isLoading = false;
  isSaving = false;

  bannerPreviews: { [key: string]: string } = {
    banner1: null,
    banner2: null,
    banner3: null,
  };

  pendingFiles: { [key: string]: File } = {
    banner1: null,
    banner2: null,
    banner3: null,
  };

  constructor(
    private homeBannersSettingsService: HomeBannersSettingsService,
    private customHomeBannersSettingsService: CustomHomeBannersSettingsService,
    private toaster: ToasterService,
    private localizationService: LocalizationService,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.homeBannersSettingsService.getHomeBanners().subscribe({
      next: (dto) => {
        this.buildForm(dto);
        this.loadPreviews(dto);
        this.isLoading = false;
      },
      error: () => {
        this.buildForm({ banner1: { isEnabled: false }, banner2: { isEnabled: false }, banner3: { isEnabled: false } } as HomeBannersDto);
        this.isLoading = false;
      }
    });
  }

  buildForm(dto: HomeBannersDto) {
    this.form = this.fb.group({
      banner1Url: [dto?.banner1?.url || ''],
      banner1IsEnabled: [dto?.banner1?.isEnabled ?? false],
      banner1ImageId: [dto?.banner1?.imageId || ''],
      banner1Description: [dto?.banner1?.description || ''],
      banner2Url: [dto?.banner2?.url || ''],
      banner2IsEnabled: [dto?.banner2?.isEnabled ?? false],
      banner2ImageId: [dto?.banner2?.imageId || ''],
      banner2Description: [dto?.banner2?.description || ''],
      banner3Url: [dto?.banner3?.url || ''],
      banner3IsEnabled: [dto?.banner3?.isEnabled ?? false],
      banner3ImageId: [dto?.banner3?.imageId || ''],
      banner3Description: [dto?.banner3?.description || ''],
    });
  }

  loadPreviews(dto: HomeBannersDto) {
    if (dto?.banner1?.imageId && dto.banner1.imageDownloadToken) {
      this.bannerPreviews['banner1'] = this.buildImageUrl(dto.banner1.imageId, dto.banner1.imageDownloadToken);
    }
    if (dto?.banner2?.imageId && dto.banner2.imageDownloadToken) {
      this.bannerPreviews['banner2'] = this.buildImageUrl(dto.banner2.imageId, dto.banner2.imageDownloadToken);
    }
    if (dto?.banner3?.imageId && dto.banner3.imageDownloadToken) {
      this.bannerPreviews['banner3'] = this.buildImageUrl(dto.banner3.imageId, dto.banner3.imageDownloadToken);
    }
  }

  buildImageUrl(fileId: string, token: string): string {
    return fileApis.bannerImageApi
      .replace('{fileId}', fileId)
      .replace('{token}', token);
  }

  onFileChanged(event: Event, bannerKey: string) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.pendingFiles[bannerKey] = file;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.bannerPreviews[bannerKey] = e.target.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  async save() {
    this.isSaving = true;

    try {
      await this.uploadPendingFiles();

      const dto: HomeBannersDto = {
        banner1: {
          imageId: this.form.get('banner1ImageId').value || null,
          url: this.form.get('banner1Url').value || null,
          description: this.form.get('banner1Description').value || null,
          isEnabled: this.form.get('banner1IsEnabled').value,
        },
        banner2: {
          imageId: this.form.get('banner2ImageId').value || null,
          url: this.form.get('banner2Url').value || null,
          description: this.form.get('banner2Description').value || null,
          isEnabled: this.form.get('banner2IsEnabled').value,
        },
        banner3: {
          imageId: this.form.get('banner3ImageId').value || null,
          url: this.form.get('banner3Url').value || null,
          description: this.form.get('banner3Description').value || null,
          isEnabled: this.form.get('banner3IsEnabled').value,
        },
      };

      this.homeBannersSettingsService.setHomeBanners(dto).subscribe({
        next: () => {
          this.toaster.success(this.localizationService.instant('::HomeBanners:Settings:Success'));
          this.isSaving = false;
        },
        error: () => {
          this.toaster.error(this.localizationService.instant('::HomeBanners:Settings:Error'));
          this.isSaving = false;
        },
      });
    } catch {
      this.toaster.error(this.localizationService.instant('::HomeBanners:Settings:Error'));
      this.isSaving = false;
    }
  }

  private async uploadPendingFiles() {
    const bannerKeys = ['banner1', 'banner2', 'banner3'];

    for (let i = 0; i < bannerKeys.length; i++) {
      const bannerKey = bannerKeys[i];
      const file = this.pendingFiles[bannerKey];
      if (!file) continue;

      const imageId = await this.customHomeBannersSettingsService.uploadBannerImage(file, i + 1).toPromise();

      this.form.get(`${bannerKey}ImageId`).setValue(imageId);
      this.pendingFiles[bannerKey] = null;
    }
  }

  clearImage(bannerKey: string) {
    this.bannerPreviews[bannerKey] = null;
    this.pendingFiles[bannerKey] = null;
    this.form.get(`${bannerKey}ImageId`).setValue('');
  }
}
